/**
 * ルーティングパス定義 + サイドバー構成。
 *
 * 仕様書: docs/01_spec/spec_user_management.md
 *
 * FEATURES（shared/src/features.ts）が単一ソース・オブ・トゥルース。
 * - サイドバー（components/layout/Sidebar.tsx）は FEATURES + usePermission で描画
 * - ロール設定マトリックス UI も FEATURES から動的に行列を生成
 * - 既存コードからの参照のため、paths オブジェクトと NavItem/NavGroup 型は維持
 */
import { FEATURES } from '@goenhub/shared';
import type { Role } from '@goenhub/shared';

/**
 * 既存コードが参照する path 定数。FEATURES に存在しないルート（公開ページ・編集詳細など）も含む。
 * v3.x の段階では FEATURES のリーフメニュー path と一致させつつ、詳細ページ等の補助 path もここに残す。
 */
export const paths = {
  dashboard: '/',

  // 掲示板 (v3.5 Board)
  board: '/board',
  boardNew: '/board/new',
  boardPostDetail: '/board/:postId',
  boardPostEdit: '/board/:postId/edit',

  // キャンペーン
  campaigns: '/campaigns',
  campaignBoard: '/campaigns/:id',

  // ランディング
  landings: '/landings',
  landingEdit: '/landings/:id',
  landingPreview: '/landings/:id/preview',
  publicLanding: '/l/:slug',

  // 広告画像生成
  adImages: '/ad-images',
  adImageEdit: '/ad-images/:id',

  // フォーム
  forms: '/forms',
  formBuilder: '/forms/:id',
  formSettings: '/forms/:id/settings',
  formCompletion: '/forms/:id/completion',
  formSubmissions: '/forms/:id/submissions',
  publicForm: '/public/forms/:id',
  liffForm: '/liff/form',

  // リフェラル
  referral: '/referral',
  urlGenerator: '/url-generator',
  referralChannels: '/referral-channels',

  // LINE Bot 系
  lineNews: '/line/news',
  lineUsers: '/line/users',
  lineSessions: '/line/sessions',
  lineEvents: '/line/events',
  lineDeliveries: '/line/deliveries',
  lineOfferings: '/line/offerings',
  lineQaReview: '/line/qa-review',

  // 統一プロンプト管理
  promptTemplates: '/prompts/templates',
  promptPresets: '/prompts/presets',

  // Mail 系
  mailGenerate: '/mail/generate',
  mailList: '/mail/list',
  mailContacts: '/mail/contacts',
  mailSchedules: '/mail/schedules',
  mailAnalysis: '/mail/analysis',
  mailSettings: '/mail/settings',

  // 営業ツール マスタ管理
  products: '/products',
  productBundles: '/product-bundles',
  productBundleNew: '/product-bundles/new',
  productBundleEdit: '/product-bundles/:id',
  businessMailTemplates: '/mail/business-templates',

  // 営業・取引
  deals: '/deals',
  dealsInbox: '/deals/inbox',
  dealsInboxDetail: '/deals/inbox/:id',
  dealDetail: '/deals/:id',
  salesContacts: '/sales/contacts',
  salesContactDetail: '/sales/contacts/:contactId',

  // 個別説明
  briefings: '/briefings',
  briefingRecord: '/briefings/record/:dealId',

  // マーケティング
  leads: '/marketing/leads',

  // 営業ツール 後工程
  quotes: '/quotes',
  quoteDetail: '/quotes/:dealId/:quoteId',
  invoices: '/invoices',
  purchaseOrders: '/purchase-orders',
  deliveries: '/deliveries',
  soldDevices: '/sold-devices',
  salesSettings: '/sales/settings',
  // 報酬設定 (v3.2.5 / T5)
  commissions: '/sales/commissions',
  /** セット → カテゴリ選択 (v3.2.6) */
  commissionSetCategories: '/sales/commissions/:setId',
  /** カテゴリ別 報酬編集画面 (v3.2.6) */
  commissionCategoryEditor: '/sales/commissions/:setId/c/:categoryId',
  /** 報酬台帳 (v3.2.8 / T6) */
  commissionLedger: '/sales/commission-ledger',
  /** SA 請求書 管理者用一覧 (v3.2.14 / Phase 3a) */
  saInvoices: '/sales/sa-invoices',
  /** 外部SA 管理 (v3.2.30): オンボーディング・契約・通知 */
  saManagement: '/sales/sa-management',
  /** セールスエージェント / 社員 ダッシュボード (v3.2.11 / SA-1) */
  saDashboard: '/sa/dashboard',
  /** SA Phase 2 (v3.2.12) */
  saDeals: '/sa/deals',
  saForms: '/sa/forms',
  saCatalogs: '/sa/catalogs',
  saDocuments: '/sa/documents',
  saCommissionCalc: '/sa/commissions',
  /** SA / 社員 自身のプロフィール編集 (v3.2.14 / Phase 3a) */
  saProfile: '/sa/profile',
  /** SA 担当顧客一覧 + 新規追加 (v3.2.23+) */
  saContacts: '/sa/contacts',
  /** SA 担当顧客の詳細 (2026-08-06) */
  saContactDetail: '/sa/contacts/:contactId',

  // 公開
  publicQuote: '/quote/:token',
  publicInvoice: '/invoice/:token',
  publicFormAnalytics: '/analytics/:token',
  /** SA 請求書 公開閲覧 (v3.2.14 / Phase 3a) */
  publicSaInvoice: '/c/sa-invoice/:token',

  // 資料管理
  documents: '/documents',
  documentEdit: '/documents/:id',
  publicDocument: '/docs/:slug',
  /** v3.2.28+: PDF ダウンロード型資料のビューワー画面 */
  pdfDocumentViewer: '/documents/pdf/:id',

  // 同意書管理 (v3.2.25)
  consentDocuments: '/consent-documents',
  consentDocumentEdit: '/consent-documents/:id',
  publicConsent: '/consent/:dealId/:linkId',

  // 公開: 招待リンク（未認証可、SSO ログインへ誘導）
  publicInvite: '/invite/:inviteId',

  // システム管理（旧 運用管理 / 共通）
  adminUsers: '/admin/users',
  /** ユーザー詳細 (v3.2.7 / T7) */
  adminUserDetail: '/admin/users/:uid',
  dealsTrash: '/admin/trash',
  staleDeals: '/admin/stale',
  settings: '/settings',
  systemLogs: '/system-logs',

  // ─────────────────────────────────────────────
  // v3.3 IA 刷新: Hub (親メニュー) パス
  // タブ切り替えは `?tab=...` クエリで行う。
  // 旧パスは Sidebar からのリンクとしては Hub に置き換わるが、
  // 直接アクセス互換のためルート (App.tsx) では旧パスを Hub への
  // リダイレクトとして残す。
  // ─────────────────────────────────────────────
  /** ✨ キャンペーン Hub */
  campaignsHub: '/campaigns',
  /** 📧 メール Hub */
  mailHub: '/mail',
  /** 💬 LINE Bot Hub */
  lineHub: '/line',
  /** 👥 オーディエンス Hub */
  audienceHub: '/audience',
  /** 🧠 AI プロンプト Hub */
  promptsHub: '/prompts',
  /** ❓ お問合せ自動化 Hub */
  inquiryHub: '/inquiry',
  /** 🗂️ 商談 Hub */
  dealsHub: '/deals',
  /** 🤝 顧客 Hub */
  customersHub: '/customers',
  /** 📦 受発注 Hub */
  ordersHub: '/orders',
  /** 💰 経理 Hub */
  financeHub: '/finance',
  /** 🎯 SA ポータル Hub (カード UI) */
  saHub: '/sa',
  /** 🏷 商品 Hub */
  productsHub: '/master/products',
  /** ⚙️ 営業・通知設定 Hub */
  salesConfigHub: '/master/sales-config',
  /** 🔧 システム Hub */
  adminHub: '/admin',
} as const;

export type PathKey = keyof typeof paths;

/**
 * 既存サイドバーが参照していた NavItem / NavGroup 型・navGroups エクスポート。
 * 新サイドバーは FEATURES を直接使うが、既存呼出しは保持する。
 */
export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: string;
  minRole?: Role;
  /** FEATURES の featureId（usePermission の判定に使用）。 */
  featureId: string;
}

export interface NavGroup {
  groupLabel: string;
  groupId: string;
  emoji?: string;
  items: NavItem[];
}

export const navGroups: NavGroup[] = FEATURES.map((group) => ({
  groupId: group.id,
  groupLabel: group.label,
  emoji: group.emoji,
  items: group.items.map((item) => ({
    key: item.id,
    label: item.label,
    path: item.path,
    icon: item.icon,
    featureId: item.id,
  })),
}));
